<template>
  <Sidebar collapsible="icon" data-test-id="navigation-menu">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" class="cursor-default hover:bg-transparent">
            <img
              v-if="whiteLabelRef.logoSmall || whiteLabelRef.logo"
              :src="whiteLabelRef.logoSmall || whiteLabelRef.logo"
              alt="logo"
              class="!h-8 !w-8 shrink-0 rounded-md object-contain"
            />
            <span class="text-foreground font-semibold truncate">{{ whiteLabelRef.companyName }}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <SidebarContent>
      <nav :aria-label="t('ui.layout.nav.ariaLabel')">
        <SidebarGroup>
          <SidebarMenu>
            <template v-if="userActivePermissionGroupRef === PermissionsGroupsEnumType.ADMIN">
              <SidebarMenuItem>
                <SidebarMenuButton as-child :is-active="isAdminDashboardActive">
                  <router-link :to="ROUTES.ADMIN.DASHBOARD">
                    <LayoutDashboardIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.dashboard') }}</span>
                  </router-link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton as-child :is-active="isAdminGroupsActive">
                  <router-link :to="ROUTES.ADMIN.GROUPS">
                    <UsersIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.groups') }}</span>
                  </router-link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton as-child :is-active="isAdminUsersActive">
                  <router-link :to="ROUTES.ADMIN.USERS">
                    <UserIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.users') }}</span>
                  </router-link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton as-child :is-active="isAdminSessionsActive">
                  <router-link :to="ROUTES.ADMIN.SESSIONS">
                    <CalendarIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.sessions') }}</span>
                  </router-link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem v-if="isPlatformAdminRef">
                <SidebarMenuButton as-child :is-active="isAdminSettingsActive">
                  <router-link :to="ROUTES.ADMIN.SETTINGS" data-test-id="platform-settings-nav-link">
                    <SettingsIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.platformSettings') }}</span>
                  </router-link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </template>

            <template v-else>
              <SidebarMenuItem>
                <SidebarMenuButton as-child :is-active="isDashboardActive">
                  <router-link :to="ROUTES.DASHBOARD" data-test-id="dashboard-nav-link">
                    <LayoutDashboardIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.dashboard') }}</span>
                  </router-link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton as-child :is-active="isSessionsActive">
                  <router-link :to="ROUTES.SESSIONS">
                    <CalendarIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.sessions') }}</span>
                  </router-link>
                </SidebarMenuButton>
                <SidebarMenuBadge v-if="sessionsNotificationsRef > 0" data-test-id="notifications-badge">{{
                  sessionsNotificationsRef
                }}</SidebarMenuBadge>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton as-child :is-active="isMessagesActive">
                  <router-link :to="ROUTES.CHAT()">
                    <MessageSquareIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.messages') }}</span>
                  </router-link>
                </SidebarMenuButton>
                <SidebarMenuBadge v-if="messagesNotificationsRef > 0" data-test-id="notifications-badge">{{
                  messagesNotificationsRef
                }}</SidebarMenuBadge>
              </SidebarMenuItem>
              <SidebarMenuItem v-if="externalResourcesUrlRef">
                <SidebarMenuButton as-child>
                  <a
                    :href="externalResourcesUrlRef"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-test-id="resources-nav-link"
                  >
                    <ExternalLinkIcon class="size-4" />
                    <span>{{ t('ui.layout.nav.resources') }}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </template>

            <SidebarMenuItem>
              <SidebarMenuButton as-child>
                <a role="button" href="#brevoConversationsExpanded">
                  <LifeBuoyIcon class="size-4" />
                  <span>{{ t('ui.layout.nav.support') }}</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </nav>
    </SidebarContent>

    <SidebarFooter>
      <SetupNavProgress />
      <NextSession />
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <SidebarMenuButton size="lg" data-test-id="navbar-avatar">
                <ShadcnAvatar data-test-id="avatar" class="size-8">
                  <ShadcnAvatarImage
                    v-if="accountRef?.picture"
                    :src="PROFILE_PICTURE_BUCKET_URL + accountRef.picture"
                    :alt="`${accountRef?.firstName} ${accountRef?.lastName}`"
                  />
                  <ShadcnAvatarFallback :class="[avatarColorClass, 'text-white']"
                    >{{ accountRef?.firstName?.[0] }}{{ accountRef?.lastName?.[0] }}</ShadcnAvatarFallback
                  >
                </ShadcnAvatar>
                <div class="grid flex-1 text-left text-sm leading-tight">
                  <span class="truncate font-medium">{{ accountRef?.firstName }} {{ accountRef?.lastName }}</span>
                  <span class="text-muted-foreground truncate text-xs">{{ accountRef?.email }}</span>
                </div>
                <EllipsisVerticalIcon class="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side="right"
              align="end"
              :side-offset="4"
            >
              <DropdownMenuLabel class="p-0 font-normal">
                <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <ShadcnAvatar class="size-8">
                    <ShadcnAvatarImage
                      v-if="accountRef?.picture"
                      :src="PROFILE_PICTURE_BUCKET_URL + accountRef.picture"
                      :alt="`${accountRef?.firstName} ${accountRef?.lastName}`"
                    />
                    <ShadcnAvatarFallback :class="[avatarColorClass, 'text-white']"
                      >{{ accountRef?.firstName?.[0] }}{{ accountRef?.lastName?.[0] }}</ShadcnAvatarFallback
                    >
                  </ShadcnAvatar>
                  <div class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-medium">{{ accountRef?.firstName }} {{ accountRef?.lastName }}</span>
                    <span class="text-muted-foreground truncate text-xs">{{ accountRef?.email }}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem data-test-id="more-actions-menu" @click="goToProfile">
                  <UserCircleIcon class="size-4" />
                  {{ t('moreActions.account.seeProfile') }}
                </DropdownMenuItem>
                <DropdownMenuItem v-if="canSwitchToAdmin" data-test-id="more-actions-menu" @click="switchToAdmin">
                  <ArrowLeftRightIcon class="size-4" />
                  {{ t('moreActions.account.switchPlatform.admin') }}
                </DropdownMenuItem>
                <DropdownMenuItem v-if="canSwitchToUser" data-test-id="more-actions-menu" @click="switchToUser">
                  <ArrowLeftRightIcon class="size-4" />
                  {{ t('moreActions.account.switchPlatform.user') }}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-test-id="more-actions-menu" variant="destructive" @click="handleLogout">
                <LogOutIcon class="size-4" />
                {{ t('moreActions.account.logout') }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>

    <SidebarRail />
  </Sidebar>
</template>

<script setup lang="ts">
import {
  ArrowLeftRightIcon,
  CalendarIcon,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  LayoutDashboardIcon,
  LifeBuoyIcon,
  LogOutIcon,
  MessageSquareIcon,
  SettingsIcon,
  UserCircleIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-vue-next'
import { computed } from 'vue'

import {
  Avatar as ShadcnAvatar,
  AvatarFallback as ShadcnAvatarFallback,
  AvatarImage as ShadcnAvatarImage,
} from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import SetupNavProgress from '@/features/admin/platform-setup/components/setup-nav-progress.vue'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { selectGetUserActivePermissionGroup } from '@/features/auth/stores/auth/auth.selectors'
import authStore from '@/features/auth/stores/auth/auth.store'
import { PermissionsGroupsEnumType, RoleEnumTypeV2 } from '@/features/auth/types'
import logout from '@/features/auth/utils/logout'
import NextSession from '@/features/ui/components/layout/main-nav/next-session/next-session.vue'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useNotifications from '@/hooks/use-notifications.hook'
import useRouter from '@/hooks/use-router.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { PROFILE_PICTURE_BUCKET_URL, ROUTES } from '@/utils/config/constants'
import { AVATAR_BG_CLASSES } from '@/utils/misc/color-palette'

const { t } = useI18n()
const router = useRouter()
const { accountRef } = useAccount()
const { whiteLabelRef } = useWhiteLabel()
const { programRolesRef } = useProgramRoles()
const userActivePermissionGroupRef = selectGetUserActivePermissionGroup(authStore)

const { messagesNotificationsRef, sessionsNotificationsRef } = useNotifications()

const externalResourcesUrlRef = computed(() => whiteLabelRef.value?.externalResourcesUrl)

const isAdminDashboardActive = computed(() => router?.currentRoute.path === ROUTES.ADMIN.DASHBOARD)
const isAdminGroupsActive = computed(() => router?.currentRoute.path === ROUTES.ADMIN.GROUPS)
const isAdminUsersActive = computed(() => router?.currentRoute.path === ROUTES.ADMIN.USERS)
const isAdminSessionsActive = computed(() => router?.currentRoute.path === ROUTES.ADMIN.SESSIONS)
const isAdminSettingsActive = computed(
  () =>
    router?.currentRoute.path?.startsWith(ROUTES.ADMIN.SETTINGS) || router?.currentRoute.path === ROUTES.ADMIN.SETUP,
)
// « Ma plateforme » est réservée aux super admins (ADMIN + SUPPORT), comme côté API.
const isPlatformAdminRef = computed(
  () =>
    !!programRolesRef.value?.includes(RoleEnumTypeV2.ADMIN) &&
    !!programRolesRef.value?.includes(RoleEnumTypeV2.SUPPORT),
)
const isDashboardActive = computed(() => router?.currentRoute.path === ROUTES.DASHBOARD)
const isSessionsActive = computed(() => router?.currentRoute.path === ROUTES.SESSIONS)
const isMessagesActive = computed(() => router?.currentRoute.path?.includes(ROUTES.CHAT()) || false)

function goToProfile() {
  router.push(ROUTES.PROFILE).catch(() => {})
}

const canSwitchToAdmin = computed(() => {
  const hasAccess =
    programRolesRef.value?.includes(RoleEnumTypeV2.ADMIN) || programRolesRef.value?.includes(RoleEnumTypeV2.MANAGER)
  return hasAccess && userActivePermissionGroupRef.value === PermissionsGroupsEnumType.USER
})

const canSwitchToUser = computed(() => {
  const hasAccess =
    programRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) || programRolesRef.value?.includes(RoleEnumTypeV2.MENTEE)
  return hasAccess && userActivePermissionGroupRef.value === PermissionsGroupsEnumType.ADMIN
})

function switchToAdmin() {
  authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.ADMIN)
  window.location.pathname = ROUTES.REDIRECT
}

function switchToUser() {
  authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.USER)
  window.location.pathname = ROUTES.REDIRECT
}

function handleLogout() {
  logout()
}

const avatarColorClass = computed(() => {
  const name = `${accountRef.value?.firstName ?? ''}${accountRef.value?.lastName ?? ''}`
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return AVATAR_BG_CLASSES[hash % AVATAR_BG_CLASSES.length]
})
</script>

<style>
[data-sidebar='sidebar'] a {
  text-decoration: none;
}

[data-sidebar='sidebar'] a:not([data-active='true']) {
  color: var(--sidebar-foreground) !important;
}

[data-sidebar='sidebar'] a[data-active='true'] {
  color: var(--sidebar-accent-foreground) !important;
}

[data-sidebar='sidebar'] img {
  width: auto;
  height: auto;
}

[data-sidebar='sidebar'] {
  background-color: #f4f4f6;
}

[data-slot='sidebar-content'] {
  --sidebar-accent: rgba(var(--color-white-label-primary), 0.08);
  --sidebar-accent-foreground: rgb(var(--color-white-label-primary));
}

[data-slot='sidebar-content'] a:not([data-active='true']):hover {
  --sidebar-accent: #dfdfe1;
  --sidebar-accent-foreground: var(--sidebar-foreground);
}

[data-slot='sidebar-footer'] [data-sidebar='menu-button']:hover {
  --sidebar-accent: #e4e4e8;
}
</style>
